import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Typography,
} from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ContentSkeleton from "../../../components/common/ContentSkeleton";
import { configApi, salesApi, sellerApi } from "../../../services/api";
import { RootState } from "../../../store";
import { updateTokenBalance } from "../../../store/slices/authSlice";
import type { SellTokenFormData } from "../../../types/sales.types";
import type { SellerBasicInfo } from "../../../types/seller.types";

const { Option } = Select;
const { Text } = Typography;

const paymentMethods = [
  { value: "CASH", label: "Cash" },
  { value: "BANK", label: "Bank Transfer" },
  { value: "MOBILE_BANKING", label: "Mobile Banking" },
  { value: "DIGITAL_WALLET", label: "Digital Wallet" },
  { value: "CRYPTO", label: "Crypto" },
  { value: "OTHER", label: "Other" },
];

const SellTokenPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [baseTokenPrice, setBaseTokenPrice] = useState<number>(300);
  const [dynamicTokenPrice, setDynamicTokenPrice] = useState<number>(0);
  const [sellers, setSellers] = useState<SellerBasicInfo[]>([]);
  const [totalDueAmount, setTotalDueAmount] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();
  const sellerId = location.state?.sellerId;
  const sellerName = location.state?.sellerName;
  const sellerPhone = location.state?.sellerPhone;
  const { user, permissions } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();



  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Get token price
        const priceResponse = await configApi.getByKey("token.price");

        if (priceResponse.status === "SUCCESS") {
          const price = parseFloat(priceResponse.data.value);
          setBaseTokenPrice(price);
          setDynamicTokenPrice(price);
        }



        const userRole = user?.designation?.toUpperCase();
        if (!!!sellerId) {
          try {
            const sellersResponse = await sellerApi.getSellers();
            if (sellersResponse.status === "SUCCESS") {
              setSellers(sellersResponse.data);
            } else {
              message.error("Failed to fetch sellers");
            }
          } catch (error) {
            message.error("Failed to fetch sellers");
          }
        } else {
          const sellerInfo: SellerBasicInfo = {
            id: sellerId,
            name: sellerName || "",
            phone: sellerPhone || "",
            // Set a placeholder designation based on user role
            designation:
              userRole === "ADMIN"
                ? "DISTRIBUTOR"
                : userRole === "DISTRIBUTOR"
                ? "DEALER"
                : userRole === "DEALER"
                ? "RETAILER"
                : "N/A",
          };
          setSellers([sellerInfo]);
        }
      } catch (error) {
        message.error("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.designation]);

  useEffect(() => {
    // Check if we have a default seller from the URL parameters
    if (sellerId && sellers.length > 0) {
      form.setFieldValue("sellerId", sellerId);
      handleSellerChange(sellerId);
    }
  }, [sellers, sellerId]);

  const handleNumberOfTokensChange = (value: number | null) => {
    if (value) {
      const totalPayable = dynamicTokenPrice * value;
      form.setFieldValue("totalPayable", totalPayable.toFixed(2));
    } else {
      form.setFieldValue("totalPayable", 0);
    }
  };

  const handleSellerChange = async (value: number) => {
    const numberOfTokens = form.getFieldValue("numberOfTokens");

    // Fetch total due amount for the selected seller
    try {
      const response = await salesApi.getTotalDueAmount(value);
      if (response.status === "SUCCESS") {
        setTotalDueAmount(response.data);
      }
    } catch (error) {
      console.error("Error fetching total due amount:", error);
      setTotalDueAmount(0);
    }

    if (numberOfTokens) {
      const totalPayable = dynamicTokenPrice * numberOfTokens;
      form.setFieldValue("totalPayable", totalPayable.toFixed(2));
    }
  };

  const onFinish = async (values: SellTokenFormData) => {
    try {
      setLoading(true);

      const numberOfTokens = values.numberOfTokens;
      const seller = sellers.find((s) => s.id == (values.sellerId || sellerId));
      const receiverDesignation = seller?.designation;

      // Use current dynamic price

      // Use returned price for accurate calculation
      // const totalPayable = calculatedPrice * numberOfTokens;

      // Ensure paid amount doesn't exceed total payable
      // const paidAmount = Math.min(values.paidAmount || 0, totalPayable);

      const formData = {
        ...values,
        sellerId: values.sellerId || sellerId,
        // totalPayable: totalPayable,
        // paidAmount: paidAmount,
        transactionDate: dayjs(values.transactionDate).format("DD-MM-YYYY"),
        reviewStatus: "PENDING",
      };

      const response = await salesApi.sellToken(formData);

      if (response.status === "SUCCESS") {
        message.success("Tokens shared successfully!");
        // Update token balance in Redux store
        if (user) {
          dispatch(
            updateTokenBalance((user.tokenBalance || 0) - numberOfTokens)
          );
        }
        form.resetFields();
        form.setFieldsValue({
          transactionDate: dayjs(),
          sellerId: sellerId,
        });
        if (sellerId) {
          handleSellerChange(sellerId);
        } else {
          setTotalDueAmount(0);
        }
      } else {
        message.error(response.message);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to sell token";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentMethodChange = (value: string) => {
    if (value === "CASH") {
      form.setFieldValue("transactionId", undefined);
    }
  };

  const getReceiverMessage = () => {
    const userRole = user?.designation?.toUpperCase();
    switch (userRole) {
      case "ADMIN":
        return "Please select a distributor";
      case "DISTRIBUTOR":
        return "Please select a dealer";
      case "DEALER":
        return "Please select a retailer";
      default:
        return "Please select a receiver";
    }
  };

  if (loading) {
    return <ContentSkeleton cardCount={1} rows={8} />;
  }

  return (
    <Card
      title="Sell Tokens"
      extra={<Button onClick={() => navigate(-1)}>Back</Button>}
      style={{ maxWidth: 800, margin: "0 auto" }}
    >
      {user && (
        <div
          style={{
            marginBottom: 24,
            textAlign: "center",
            background: "#f8f9fa",
            padding: "16px",
            borderRadius: "4px",
          }}
        >
          <Text strong style={{ fontSize: 24, fontWeight: "bold" }}>
            Token Balance:
          </Text>{" "}
          <Text style={{ fontSize: 24, fontWeight: "bold", color: "#4096ff" }}>
            {user.tokenBalance || 0}
          </Text>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          sellerId: sellerId,
          transactionDate: dayjs(),
          paymentMethod: "CASH",
          paidAmount: 0,
        }}
      >
        <Form.Item
          name="sellerId"
          label="Select Receiver"
          rules={[{ required: true, message: getReceiverMessage() }]}
          help={
            totalDueAmount > 0 ? (
              <Text type="danger">
                Previous due: ৳{totalDueAmount.toLocaleString()}
              </Text>
            ) : undefined
          }
        >
          <Select
            placeholder="Select Receiver"
            loading={loading}
            disabled={!!sellerId}
            showSearch
            optionFilterProp="children"
            onChange={handleSellerChange}
          >
            {sellers.map((seller) => (
              <Option key={seller.id} value={seller.id}>
                {seller.name} - {seller.phone}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="numberOfTokens"
          label={`Number of Tokens (৳${dynamicTokenPrice.toFixed(
            2
          )} per token)`}

          rules={[
            { required: true, message: "Please enter number of tokens" },
            {
              type: "number",
              min: 1,
              message: "Number must be greater than 0",
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || !user) {
                  return Promise.resolve();
                }
                if (value > (user.tokenBalance || 0)) {
                  return Promise.reject(
                    new Error("You do not have enough tokens")
                  );
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="Enter number of tokens"
            // onChange={handleNumberOfTokensChange}
          />
        </Form.Item>

        <Form.Item name="totalPayable" label="Total Payable"
           rules={[
               {required: true, message: "Please enter total payable amount"},
               {
                   type: "number",
                   min: 1,
                   message: "Total payable amount must be greater than 0",
               }
           ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            formatter={(value) =>
              `৳ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) => value!.replace(/৳\s?|(,*)/g, "")}
          />
        </Form.Item>

        <Form.Item
          name="paidAmount"
          label="Paid Amount"
        >
          <InputNumber
            style={{ width: "100%" }}
            formatter={(value) =>
              `৳ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value) => value!.replace(/৳\s?|(,*)/g, "")}
          />
        </Form.Item>

        <Form.Item
          name="paymentMethod"
          label="Payment Method"
          rules={[{ required: true, message: "Please select payment method" }]}
        >
          <Select
            placeholder="Select payment method"
            onChange={handlePaymentMethodChange}
          >
            {paymentMethods.map((method) => (
              <Option key={method.value} value={method.value}>
                {method.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="transactionDate"
          label="Transaction Date"
          rules={[
            { required: true, message: "Please select transaction date" },
          ]}
        >
          <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} block>
            Sell Tokens
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default SellTokenPage;
